/**
 * Crew endpoints for odl_app.
 *
 * The crews grid lists via EntityGrid (dataSource/apiClient); the query here
 * serves pick-lists (e.g. the services site-grid crew filter), not the grid.
 * This module also injects create / patch / remove (soft-delete) and the
 * crew-extra-pay sub-resource mutations into entitiesApi, and re-exports the
 * generated hooks for component use.
 *
 * Extra-pay lives under /crewextrapay/ (not /api/odl-app/) so those endpoints
 * use queryFn with apiClient directly rather than the shared axiosBaseQuery.
 *
 * Contains:
 * - crewsApi           — the injected endpoint slice
 * - hook re-exports    — useListCrewsQuery, useCreateCrewMutation,
 *                        usePatchCrewMutation, useRemoveCrewMutation,
 *                        useListCrewExtraPayQuery, useCreateCrewExtraPayMutation,
 *                        usePatchCrewExtraPayMutation
 */

// eslint-disable-next-line no-restricted-imports -- documented queryFn escape hatch outside the entities API convention (see file header)
import apiClient from '@sophocles/http-common';

import { entitiesApi } from './entitiesApi';

const crewsApi = entitiesApi.injectEndpoints({
  endpoints: build => ({
    /**
     * List crews for pick-lists. Cached under the Crew LIST tag, so crew
     * create/patch/remove mutations refresh the options automatically.
     *
     * @param {object} [params] - odl-app list params (filter[...], sort, limit).
     */
    listCrews: build.query({
      query: (params = {}) => ({
        url: 'crews/',
        params,
      }),
      transformResponse: b => b.data,
      providesTags: [{ type: 'Crew', id: 'LIST' }],
    }),

    /**
     * Create a new crew.
     *
     * @param {{ body: object }} arg
     */
    createCrew: build.mutation({
      query: ({ body }) => ({
        url: 'crews/',
        method: 'post',
        data: body,
      }),
      transformResponse: b => b.data,
      invalidatesTags: [{ type: 'Crew', id: 'LIST' }],
    }),

    /**
     * Partial update a crew.
     *
     * @param {{ id: string|number, body: object }} arg
     */
    patchCrew: build.mutation({
      query: ({ id, body }) => ({
        url: `crews/${id}/`,
        method: 'patch',
        data: body,
      }),
      transformResponse: b => b.data,
      invalidatesTags: (result, error, { id }) => [
        { type: 'Crew', id: 'LIST' },
        { type: 'Crew', id },
      ],
    }),

    /**
     * Soft-delete a crew (DELETE on the backend sets active=false).
     *
     * @param {{ id: string|number }} arg
     */
    removeCrew: build.mutation({
      query: ({ id }) => ({
        url: `crews/${id}/`,
        method: 'delete',
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Crew', id: 'LIST' },
        { type: 'Crew', id },
      ],
    }),

    // -------------------------------------------------------------------------
    // Crew extra-pay sub-resource  (/crewextrapay/ — NOT under odl-app/)
    //
    // These use queryFn + apiClient directly because the path is outside the
    // odl-app/ namespace that axiosBaseQuery auto-prefixes.
    // -------------------------------------------------------------------------

    /**
     * List extra-pay entries for one crew.
     *
     * @param {{ crewId: string|number, params?: object }} arg
     */
    listCrewExtraPay: build.query({
      queryFn: async ({ crewId, params = {} }) => {
        try {
          const res = await apiClient.get('/crewextrapay/bycrew/', {
            params: { ...params, crew_id: crewId },
          });
          return { data: res.data };
        } catch (err) {
          return { error: { status: err.response?.status, data: err.response?.data } };
        }
      },
      providesTags: (result, error, { crewId }) => [{ type: 'CrewExtraPay', id: crewId }],
    }),

    /**
     * Create a crew extra-pay entry.
     *
     * @param {{ body: object }} arg  body includes { member, extra_pay }
     */
    createCrewExtraPay: build.mutation({
      queryFn: async ({ body }) => {
        try {
          const res = await apiClient.post('/crewextrapay/', body);
          return { data: res.data };
        } catch (err) {
          return { error: { status: err.response?.status, data: err.response?.data } };
        }
      },
      invalidatesTags: (result, error, { body }) => [{ type: 'CrewExtraPay', id: body?.crew }],
    }),

    /**
     * Patch a crew extra-pay entry.
     *
     * @param {{ extraPayId: string|number, body: object }} arg
     */
    patchCrewExtraPay: build.mutation({
      queryFn: async ({ extraPayId, body }) => {
        try {
          const res = await apiClient.patch(`/crewextrapay/${extraPayId}/`, body);
          return { data: res.data };
        } catch (err) {
          return { error: { status: err.response?.status, data: err.response?.data } };
        }
      },
      invalidatesTags: (result, error, { body }) => [{ type: 'CrewExtraPay', id: body?.crew }],
    }),
  }),
});

export { crewsApi };
export const {
  useListCrewsQuery,
  useCreateCrewMutation,
  usePatchCrewMutation,
  useRemoveCrewMutation,
  useListCrewExtraPayQuery,
  useLazyListCrewExtraPayQuery,
  useCreateCrewExtraPayMutation,
  usePatchCrewExtraPayMutation,
} = crewsApi;
